import { hexToRgba, PALETTE, type PaletteKey } from '../../palette';

/**
 * Polished skins (ADR-0148): the default gun (the Pollinator) redrawn smooth, not in pixels: anti-aliased shapes with
 * the Pollinator's silhouette, frame, origin, grip and muzzle (art px of its 64×36 frame), shaded like mirror chrome (a
 * bright sky over a hard dark horizon band and a lit bounce under it, diagonal reflection streaks, bevels lit on their
 * upper edges), painted at `POLISH_RES` px an art px. Its colours are the palette's, blended. Pure: the client bakes it into textures, `tools/assetc polished` into a review sheet.
 */

/** Painted px per art px: a 64×36 frame is painted 256×144 (a 1080p screen shows it 64 px wide, mipmapped). */
export const POLISH_RES = 4;
/** The art frame it fills (the Pollinator's). */
export const POLISH_SIZE = [64, 36] as const;

/** A ramp of palette keys, dark → light. */
export type Ramp = readonly PaletteKey[];

/** A polished finish. Its `body[0]` and `trim[0]` stay dark: a gun's rim is drawn in them (crate guns too, skins.ts). */
export interface Polish {
  /** The body's metal: deep rim, the horizon's dark band, shade, mid, lit, specular. */
  body: Ramp;
  /** The stock and receiver, when they differ from the front (two-tone). */
  back?: Ramp;
  /** The collar, trigger guard, screws, muzzle crown, bezel and inlays: 5 steps. */
  trim: Ramp;
  /** The seed jar's glass: deep, shade, mid, lit. */
  glass: Ramp;
  /** The medallion's stone: dark, mid, lit. */
  gem: Ramp;
  /** What is inlaid, forged, painted or shimmers in the metal. */
  pattern?: Pattern;
  /** How hard its mirror's light and dark are: 1 (unset) chrome, less a glossy paint job. */
  gloss?: number;
  /** The ramps its pattern paints in, each as `body` (6 steps): a fade's from stock to muzzle, a swirl's, art's inks. */
  inks?: readonly Ramp[];
}

/**
 * A finish's pattern. The legends (ADR-0148 amended) are the shooters' most coveted finishes made for the garden: a
 * `fade`, the Doppler's smoky `doppler` marbling, case hardening's `patina`, the `marble` of a Candy Swirl, a crimson
 * `web`, and painted art: the `dragon` of Dragon Bloom, the howling wolf of `howl`, the feathered `serpent`, the
 * `lotus` pond and Thunder Spear's spear and `runes`.
 */
type Pattern =
  | 'scroll'
  | 'damascus'
  | 'stars'
  | 'tiger'
  | 'pearl'
  | 'prism'
  | 'fade'
  | 'doppler'
  | 'patina'
  | 'marble'
  | 'web'
  | 'dragon'
  | 'howl'
  | 'serpent'
  | 'lotus'
  | 'runes';

const CHROME: Ramp = ['k', '1', '3', '4', '5', 'w'];
const SILVER: Ramp = ['1', '2', '3', '4', '5', 'w'];
const GOLD: Ramp = ['N', 'n', 'U', 'Y', 'y', 'w'];
const GOLD_TRIM: Ramp = ['N', 'n', 'U', 'Y', 'y'];
const STEEL_TRIM: Ramp = ['k', '2', '3', '4', '5'];
const EMERALD: Ramp = ['D', 'G', 'g', 'l'];
const AQUA: Ramp = ['1', 'B', 'b', 'c'];
const RUBY: Ramp = ['R', 'r', 'i'];
const SAPPHIRE: Ramp = ['B', 'b', 'c'];
const BLUE: Ramp = ['k', '1', 'B', 'b', 'c', 'w'];
const VIOLET: Ramp = ['k', 'N', 'P', 'P', 'p', 'i'];
const SCALES: Ramp = ['k', 'D', 'G', 'g', 'l', 'y'];
/** Art's outlines: near-black however it is lit. */
const INK: Ramp = ['k', 'k', 'k', 'k', '1', '2'];

/**
 * The polished finishes, keyed as their skins without `polished_` (content `GUN_SKINS`: `polished_chrome`). Assets may
 * not import content, so the keys are duplicated: a client test checks they match.
 */
export const POLISHED = {
  chrome: { body: CHROME, trim: STEEL_TRIM, glass: AQUA, gem: SAPPHIRE },
  gold: { body: GOLD, trim: ['N', 'n', 'u', 'U', 'Y'], glass: EMERALD, gem: RUBY },
  rose: {
    body: ['N', 'n', 'm', 'i', 'e', 'w'],
    trim: ['N', 'n', 'm', 't', 'e'],
    glass: ['P', 'p', 'i', 'e'],
    gem: ['P', 'p', 'i'],
  },
  copper: { body: ['N', 'n', 'u', 'U', 't', 'e'], trim: GOLD_TRIM, glass: AQUA, gem: SAPPHIRE },
  platinum: {
    body: ['1', '3', '4', '5', 'w', 'w'],
    trim: STEEL_TRIM,
    glass: ['2', '4', '5', 'w'],
    gem: ['b', 'c', 'w'],
  },
  onyx: { body: ['k', 'k', '1', '2', '3', '5'], trim: GOLD_TRIM, glass: ['k', '1', '2', '3'], gem: RUBY },
  cobalt: { body: ['k', '1', 'B', 'b', 'c', 'w'], trim: STEEL_TRIM, glass: AQUA, gem: ['1', 'b', 'c'] },
  amethyst: {
    body: ['k', 'N', 'P', 'p', 'i', 'w'],
    trim: GOLD_TRIM,
    glass: ['k', 'P', 'p', 'i'],
    gem: ['P', 'p', 'i'],
  },
  candy: { body: ['k', 'N', 'R', 'r', 'i', 'w'], trim: STEEL_TRIM, glass: ['N', 'R', 'r', 'i'], gem: RUBY },
  pearl: {
    body: ['2', '4', '5', '5', 'w', 'w'],
    trim: GOLD_TRIM,
    glass: ['2', '4', '5', 'w'],
    gem: ['P', 'p', 'i'],
    pattern: 'pearl',
  },
  glacier: { body: ['1', 'B', 'b', 'c', '5', 'w'], trim: STEEL_TRIM, glass: ['B', 'b', 'c', 'w'], gem: SAPPHIRE },
  heat: {
    body: CHROME,
    trim: STEEL_TRIM,
    glass: AQUA,
    gem: SAPPHIRE,
    pattern: 'fade',
    // Heat-tinted titanium from the stock to the muzzle: straw, bronze, violet, blue.
    inks: [
      ['N', 'n', 'U', 'Y', 'y', 'w'],
      ['N', 'n', 'u', 'o', 'Y', 'w'],
      ['k', 'P', 'P', 'p', 'i', 'w'],
      ['k', '1', 'B', 'b', 'c', 'w'],
    ],
  },
  prism: { body: CHROME, trim: STEEL_TRIM, glass: ['P', 'p', 'i', 'w'], gem: ['P', 'p', 'i'], pattern: 'prism' },
  damascus: { body: SILVER, trim: STEEL_TRIM, glass: EMERALD, gem: RUBY, pattern: 'damascus' },
  engraved: { body: SILVER, trim: GOLD_TRIM, glass: EMERALD, gem: RUBY, pattern: 'scroll' },
  twotone: { body: CHROME, back: ['k', 'k', '1', '2', '3', '4'], trim: STEEL_TRIM, glass: AQUA, gem: SAPPHIRE },
  ivory: { body: ['N', 'n', 't', 'e', 'w', 'w'], trim: GOLD_TRIM, glass: EMERALD, gem: RUBY, pattern: 'scroll' },
  starlight: {
    body: ['k', 'k', '1', 'B', 'b', 'c'],
    trim: STEEL_TRIM,
    glass: AQUA,
    gem: ['B', 'c', 'w'],
    pattern: 'stars',
  },
  bubblegum: { body: ['N', 'P', 'p', 'i', 'e', 'w'], trim: ['1', 'B', 'b', 'c', 'w'], glass: AQUA, gem: SAPPHIRE },
  tiger: { body: GOLD, trim: ['k', 'N', 'n', 'U', 'Y'], glass: EMERALD, gem: RUBY, pattern: 'tiger' },
  // The legends (ADR-0148 amended).
  dragonlore: {
    body: ['N', 'n', 'm', 't', 'e', 'w'],
    trim: GOLD_TRIM,
    glass: EMERALD,
    gem: ['D', 'g', 'l'],
    pattern: 'dragon',
    gloss: 0.55,
    inks: [SCALES, GOLD, ['k', 'N', 'N', 'R', 'u', 'U'], INK],
  },
  bluegem: {
    body: CHROME,
    trim: GOLD_TRIM,
    glass: AQUA,
    gem: SAPPHIRE,
    pattern: 'patina',
    inks: [['k', '1', 'B', 'B', 'b', 'c'], VIOLET, GOLD],
  },
  howl: {
    body: ['k', 'N', 'R', 'r', 'r', 'o'],
    trim: STEEL_TRIM,
    glass: ['N', 'R', 'r', 'i'],
    gem: ['n', 'o', 'y'],
    pattern: 'howl',
    gloss: 0.6,
    inks: [
      ['k', 'k', '1', '2', '3', '5'],
      ['N', 'u', 'U', 'o', 'Y', 'y'],
      INK,
      ['N', 'R', 'r', 'x', 'y', 'w'],
      ['n', 'U', 'Y', 'y', 'e', 'w'],
    ],
  },
  serpent: {
    body: ['k', 'k', 'D', 'G', 'g', 'l'],
    trim: GOLD_TRIM,
    glass: EMERALD,
    gem: RUBY,
    pattern: 'serpent',
    gloss: 0.6,
    inks: [GOLD, ['N', 'R', 'R', 'r', 'r', 'i'], INK, SCALES],
  },
  lotus: {
    body: ['k', '1', 'D', 'G', 'g', 'l'],
    trim: GOLD_TRIM,
    glass: ['P', 'p', 'i', 'e'],
    gem: ['P', 'p', 'i'],
    pattern: 'lotus',
    gloss: 0.6,
    inks: [['N', 'P', 'p', 'i', 'e', 'w'], GOLD, INK, SCALES],
  },
  gungnir: {
    body: ['k', '1', 'B', 'B', 'b', 'c'],
    trim: GOLD_TRIM,
    glass: AQUA,
    gem: SAPPHIRE,
    pattern: 'runes',
    gloss: 0.8,
    inks: [GOLD, INK],
  },
  ruby: {
    body: ['k', 'N', 'R', 'r', 'x', 'i'],
    trim: STEEL_TRIM,
    glass: ['N', 'R', 'r', 'i'],
    gem: RUBY,
    pattern: 'doppler',
  },
  sapphire: { body: BLUE, trim: STEEL_TRIM, glass: AQUA, gem: SAPPHIRE, pattern: 'doppler' },
  blackpearl: {
    body: ['k', 'k', 'k', '1', 'P', 'p'],
    trim: STEEL_TRIM,
    glass: ['k', 'P', 'p', 'i'],
    gem: ['P', 'p', 'i'],
    pattern: 'doppler',
    inks: [['k', 'k', '1', 'B', 'b', 'c']],
  },
  emerald: { body: SCALES, trim: STEEL_TRIM, glass: EMERALD, gem: ['G', 'g', 'l'], pattern: 'doppler' },
  fade: {
    body: CHROME,
    trim: STEEL_TRIM,
    glass: ['P', 'p', 'i', 'e'],
    gem: ['n', 'o', 'y'],
    pattern: 'fade',
    // Anodised from the stock to the muzzle: violet, magenta, rose, gold.
    inks: [
      ['k', '1', 'B', 'P', 'p', 'i'],
      ['k', 'N', 'P', 'p', 'i', 'w'],
      ['N', 'R', 'p', 'i', 'o', 'y'],
      ['N', 'n', 'U', 'o', 'Y', 'y'],
    ],
  },
  marblefade: {
    body: CHROME,
    trim: STEEL_TRIM,
    glass: AQUA,
    gem: RUBY,
    pattern: 'marble',
    inks: [
      ['N', 'R', 'r', 'x', 'i', 'w'],
      ['n', 'U', 'Y', 'y', 'e', 'w'],
      ['k', 'B', 'b', 'b', 'c', 'w'],
    ],
  },
  crimsonweb: {
    body: ['k', 'N', 'R', 'R', 'r', 'i'],
    trim: STEEL_TRIM,
    glass: ['N', 'R', 'r', 'i'],
    gem: RUBY,
    pattern: 'web',
    gloss: 0.8,
    inks: [['k', 'k', 'k', '1', '2', '3']],
  },
} as const satisfies Record<string, Polish>;

export type PolishName = keyof typeof POLISHED;

// --- Colour -------------------------------------------------------------------------------------------------------

type RGB = readonly [number, number, number];

const WHITE: RGB = [1, 1, 1];
const BLACK: RGB = [0, 0, 0];

const rgb = (k: PaletteKey): RGB => {
  const [r, g, b] = hexToRgba(PALETTE[k]);
  return [r / 255, g / 255, b / 255];
};

const clamp = (v: number, a = 0, b = 1): number => (v < a ? a : v > b ? b : v);
const mix = (a: RGB, b: RGB, t: number): RGB => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];
const smooth = (a: number, b: number, v: number): number => {
  const t = clamp((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};

/** Step `v` of a ramp, blended between its steps; past its top, on towards white. */
function tone(r: readonly RGB[], v: number): RGB {
  const top = r.length - 1;
  if (v >= top) return mix(r[top] as RGB, WHITE, clamp(v - top));
  if (v <= 0) return r[0] as RGB;
  const i = Math.floor(v);
  return mix(r[i] as RGB, r[i + 1] as RGB, v - i);
}

/** `keys` piecewise-linear at `y`: [[y, value], …] ascending. */
function profile(keys: readonly (readonly [number, number])[], y: number): number {
  const first = keys[0] as readonly [number, number];
  if (y <= first[0]) return first[1];
  for (let i = 1; i < keys.length; i++) {
    const [y1, v1] = keys[i] as readonly [number, number];
    if (y <= y1) {
      const [y0, v0] = keys[i - 1] as readonly [number, number];
      return v0 + ((v1 - v0) * (y - y0)) / (y1 - y0);
    }
  }
  return (keys[keys.length - 1] as readonly [number, number])[1];
}

/** A deterministic 0..1 hash of an integer pair. */
const hash = (x: number, y: number, salt = 0): number => {
  let h = (x * 374761393 + y * 668265263 + salt * 2246822519) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

// --- Distances (art px, negative inside) ---------------------------------------------------------------------------

type Sdf = (x: number, y: number) => number;
type Pt = readonly [number, number];

const box =
  (x0: number, y0: number, x1: number, y1: number, r = 0): Sdf =>
  (x, y) => {
    const qx = Math.abs(x - (x0 + x1) / 2) - ((x1 - x0) / 2 - r);
    const qy = Math.abs(y - (y0 + y1) / 2) - ((y1 - y0) / 2 - r);
    return Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0) - r;
  };

const circle =
  (cx: number, cy: number, r: number): Sdf =>
  (x, y) =>
    Math.hypot(x - cx, y - cy) - r;

const ellipse =
  (cx: number, cy: number, rx: number, ry: number): Sdf =>
  (x, y) =>
    (Math.hypot((x - cx) / rx, (y - cy) / ry) - 1) * Math.min(rx, ry);

/** The distance to a polygon (Inigo Quilez's). */
const poly =
  (v: readonly Pt[]): Sdf =>
  (x, y) => {
    const [x0, y0] = v[0] as Pt;
    let d = (x - x0) ** 2 + (y - y0) ** 2;
    let s = 1;
    for (let i = 0, j = v.length - 1; i < v.length; j = i, i++) {
      const [ax, ay] = v[i] as Pt;
      const [bx, by] = v[j] as Pt;
      const ex = bx - ax;
      const ey = by - ay;
      const wx = x - ax;
      const wy = y - ay;
      const t = clamp((wx * ex + wy * ey) / (ex * ex + ey * ey || 1));
      d = Math.min(d, (wx - ex * t) ** 2 + (wy - ey * t) ** 2);
      const c1 = y >= ay;
      const c2 = y < by;
      const c3 = ex * wy > ey * wx;
      if ((c1 && c2 && c3) || (!c1 && !c2 && !c3)) s = -s;
    }
    return s * Math.sqrt(d);
  };

/** The distance to a polyline `pts`, less half its width `w`. */
const stroke =
  (pts: readonly Pt[], w: number): Sdf =>
  (x, y) => {
    let d = Number.POSITIVE_INFINITY;
    for (let i = 1; i < pts.length; i++) {
      const [ax, ay] = pts[i - 1] as Pt;
      const [bx, by] = pts[i] as Pt;
      const ex = bx - ax;
      const ey = by - ay;
      const t = clamp(((x - ax) * ex + (y - ay) * ey) / (ex * ex + ey * ey || 1));
      d = Math.min(d, Math.hypot(x - ax - ex * t, y - ay - ey * t));
    }
    return d - w / 2;
  };

const union =
  (...s: Sdf[]): Sdf =>
  (x, y) => {
    let d = Number.POSITIVE_INFINITY;
    for (const f of s) d = Math.min(d, f(x, y));
    return d;
  };

const cut =
  (a: Sdf, b: Sdf): Sdf =>
  (x, y) =>
    Math.max(a(x, y), -b(x, y));

/** Where `s` faces at x, y: its outward normal's slope towards the light (top-left): +1 lit, −1 away. */
function facing(s: Sdf, x: number, y: number): number {
  const e = 0.12;
  const nx = s(x + e, y) - s(x - e, y);
  const ny = s(x, y + e) - s(x, y - e);
  const n = Math.hypot(nx, ny) || 1;
  return (-0.55 * nx - 0.83 * ny) / n;
}

// --- The raster ----------------------------------------------------------------------------------------------------

type Box = readonly [number, number, number, number];

/**
 * A premultiplied RGBA float raster of an art box (the gun's frame unless told; its top-left at art px `ox`, `oy`) at
 * `res` px an art px.
 */
class Raster {
  readonly w: number;
  readonly h: number;
  readonly res: number;
  readonly px: Float32Array;
  readonly ox: number;
  readonly oy: number;

  constructor(res: number, px?: Float32Array, [w, h]: readonly [number, number] = POLISH_SIZE, ox = 0, oy = 0) {
    this.res = res;
    this.w = Math.round(w * res);
    this.h = Math.round(h * res);
    this.px = px ? px.slice() : new Float32Array(this.w * this.h * 4);
    this.ox = ox;
    this.oy = oy;
  }

  /** Each px of `box` (art px) in `s`, anti-aliased over a px, painted `paint(x, y)` at `alpha`. */
  fill(b: Box, s: Sdf, paint: (x: number, y: number) => RGB, alpha = 1): void {
    const k = this.res;
    const [x0, y0, x1, y1] = [
      Math.max(0, Math.floor((b[0] - this.ox) * k)),
      Math.max(0, Math.floor((b[1] - this.oy) * k)),
      Math.min(this.w, Math.ceil((b[2] - this.ox) * k)),
      Math.min(this.h, Math.ceil((b[3] - this.oy) * k)),
    ];
    for (let py = y0; py < y1; py++)
      for (let px = x0; px < x1; px++) {
        const x = (px + 0.5) / k + this.ox;
        const y = (py + 0.5) / k + this.oy;
        const a = clamp(0.5 - s(x, y) * k) * alpha;
        if (a <= 0) continue;
        const [r, g, bl] = paint(x, y);
        const i = (py * this.w + px) * 4;
        const keep = 1 - a;
        const p = this.px;
        p[i] = r * a + (p[i] as number) * keep;
        p[i + 1] = g * a + (p[i + 1] as number) * keep;
        p[i + 2] = bl * a + (p[i + 2] as number) * keep;
        p[i + 3] = a + (p[i + 3] as number) * keep;
      }
  }

  /** As 8-bit straight-alpha RGBA. */
  painted(): Painted {
    return { rgba: this.bytes(), w: this.w, h: this.h };
  }

  bytes(): Uint8ClampedArray {
    const out = new Uint8ClampedArray(this.w * this.h * 4);
    for (let i = 0; i < out.length; i += 4) {
      const a = this.px[i + 3] as number;
      if (a <= 0) continue;
      out[i] = ((this.px[i] as number) / a) * 255 + 0.5;
      out[i + 1] = ((this.px[i + 1] as number) / a) * 255 + 0.5;
      out[i + 2] = ((this.px[i + 2] as number) / a) * 255 + 0.5;
      out[i + 3] = a * 255 + 0.5;
    }
    return out;
  }
}

// --- The gun's shapes (art px of the Pollinator's frame) -----------------------------------------------------------

const STOCK = poly([
  [7, 14],
  [22.4, 14],
  [22.4, 25.2],
  [16, 25.2],
  [6.6, 30],
  [3.4, 30],
  [2, 28.7],
  [2, 18.8],
]);
const RECEIVER = box(22, 14, 46, 26, 1);
const COLLAR = box(21.6, 11.8, 42.4, 14.3, 0.5);
const JAR = union(box(22, 7.6, 42, 12.3, 0.4), ellipse(32, 7.8, 10, 5.6));
const SHROUD = poly([
  [45.6, 16],
  [52.8, 16],
  [56.8, 17.8],
  [62, 17.8],
  [62, 22.2],
  [57.4, 22.2],
  [54.2, 24],
  [45.6, 24],
]);
const GUARD = cut(box(29.8, 25.4, 36.2, 30.2, 1.7), box(31.3, 24, 34.7, 28.7, 1));
const TRIGGER = stroke(
  [
    [32.3, 25.6],
    [32.9, 27.2],
    [33.8, 28.2],
  ],
  1.1,
);
/** The rim round the whole gun: the old guns' 2 art px. */
const RIM = 1.8;
/** Each part, and its box (art px: x0, y0, x1, y1 a part). */
const PARTS: readonly Sdf[] = [STOCK, RECEIVER, COLLAR, JAR, SHROUD, GUARD, TRIGGER];
const BOXES = [
  2, 14, 22.4, 30, 22, 14, 46, 26, 21.6, 11.8, 42.4, 14.3, 22, 2.2, 42, 12.3, 45.6, 16, 62, 24, 29.8, 25.4, 36.2, 30.2,
  31.7, 25, 34.4, 28.8,
];
/** The whole gun: only the parts whose box is within its rim are measured (past it, far enough). */
const GUN: Sdf = (x, y) => {
  let d = RIM + 2;
  const m = RIM + 1;
  for (let i = 0; i < PARTS.length; i++) {
    const o = i * 4;
    if (x < (BOXES[o] as number) - m || x > (BOXES[o + 2] as number) + m) continue;
    if (y < (BOXES[o + 1] as number) - m || y > (BOXES[o + 3] as number) + m) continue;
    d = Math.min(d, (PARTS[i] as Sdf)(x, y));
  }
  return d;
};

const ALL: Box = [0, 0, 64, 36];

/**
 * The kill counter's window (ADR-0148 amended): dark glass across the receiver's front and the shroud, under the cooling
 * slots, five seven-segment digits behind it (unlit in the gun at rest; the owner's kills lit by `paintCount`), on whole
 * painted px. As big as the gun's side has room for, so it reads on a held gun.
 */
export const COUNTER_BOX: Box = [41.5, 19.75, 53.75, 23.5];
const COUNTER_GLASS = box(...COUNTER_BOX, 0.4);
const COUNTER_RIM = box(COUNTER_BOX[0] - 0.3, COUNTER_BOX[1] - 0.3, COUNTER_BOX[2] + 0.3, COUNTER_BOX[3] + 0.3, 0.6);
/** The counter's digits: as many, so it counts to 99,999. */
const DIGITS = 5;
/** Each digit's lit segments (a b c d e f g: top, top right, bottom right, bottom, bottom left, top left, middle). */
const SEVEN = [
  0b1111110, 0b0110000, 0b1101101, 0b1111001, 0b0110011, 0b1011011, 0b1011111, 0b1110000, 0b1111111, 0b1111011,
];

/** The counter's segments showing `n` (its significant digits; −1: every segment, the unlit eights). */
function segments(n: number): Sdf {
  const [x0, y0, x1, y1] = COUNTER_BOX;
  const st = 0.42;
  const pitch = (x1 - x0 - 0.8) / DIGITS;
  const dw = pitch * 0.74;
  const top = y0 + 0.32;
  const dh = y1 - y0 - 0.64;
  const shown = n < 0 ? '' : String(Math.min(Math.floor(n), 10 ** DIGITS - 1)).padStart(DIGITS, ' ');
  const parts: Sdf[] = [];
  for (let d = 0; d < DIGITS; d++) {
    const ch = shown[d];
    const bits = n < 0 ? 0b1111111 : ch && ch !== ' ' ? (SEVEN[Number(ch)] ?? 0) : 0;
    if (!bits) continue;
    const L = x0 + 0.4 + d * pitch + (pitch - dw) / 2 + st / 2;
    const R = L + dw - st;
    const T = top + st / 2;
    const B = top + dh - st / 2;
    const M = (T + B) / 2;
    const g = st * 0.7;
    // Leaning forward a little, as a counter's digits do.
    const at = (x: number, y: number): Pt => [x + 0.14 * (M - y), y];
    const seg = [
      [at(L + g, T), at(R - g, T)],
      [at(R, T + g), at(R, M - g)],
      [at(R, M + g), at(R, B - g)],
      [at(L + g, B), at(R - g, B)],
      [at(L, M + g), at(L, B - g)],
      [at(L, T + g), at(L, M - g)],
      [at(L + g, M), at(R - g, M)],
    ] as const;
    seg.forEach((pts, i) => {
      if (bits & (1 << (6 - i))) parts.push(stroke(pts, st));
    });
  }
  return parts.length ? union(...parts) : () => Number.POSITIVE_INFINITY;
}

/** The stock's and receiver's light by row: a top face to a lit bevel, the side's sky, the hard horizon, a bounce. */
const SLAB_TOP: readonly (readonly [number, number])[] = [
  [14, 2.7],
  [15, 3.4],
  [16.4, 4.1],
  [17.05, 4.6],
  [17.3, 5.7],
  [17.6, 4.3],
  [20.4, 3.5],
  [21.9, 2.8],
  [22.15, 1],
  [23.1, 1.3],
];
const RECEIVER_ROWS = [...SLAB_TOP, [24.6, 2.4], [25.4, 3], [26, 2.2]] as const;
const STOCK_ROWS = [...SLAB_TOP, [24.6, 2.4], [26.8, 3.1], [28.8, 2.5], [30, 1.6]] as const;
/** The shroud's: a sloping top face, a specular line, the tube's sky, horizon and bounce. */
const SHROUD_ROWS = [
  [16, 3.3],
  [17.3, 4.4],
  [17.85, 5.8],
  [18.3, 4.5],
  [19.6, 3.6],
  [20.15, 2.8],
  [20.35, 1],
  [21.3, 1.4],
  [22.6, 2.6],
  [23.4, 2.1],
  [24, 1.4],
] as const;
/** The barrel's (narrower). */
const BARREL_ROWS = [
  [17.8, 4.4],
  [18.25, 5.9],
  [18.7, 4.5],
  [19.75, 3.4],
  [19.95, 1],
  [20.9, 1.5],
  [21.7, 2.6],
  [22.2, 1.8],
] as const;

/** Diagonal reflection streaks on a side from `c`: a soft wide one and a thin one after it. */
const streaks = (x: number, y: number, c: number): number => {
  const s = x + 0.85 * (y - 20);
  return 1.1 * clamp(1 - Math.abs(s - c) / 1.5) ** 0.7 + 0.9 * clamp(1 - Math.abs(s - c - 2.6) / 0.45);
};

/** A ramp's colours. */
const colours = (r: Ramp): RGB[] => r.map(rgb);

const RAINBOW: readonly RGB[] = (['r', 'o', 'y', 'l', 'c', 'b', 'p'] as const).map(rgb);

/** Smooth value noise at x, y (a lattice cell an art px), 0..1. */
function noise(x: number, y: number, salt: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const u = smooth(0, 1, x - xi);
  const w = smooth(0, 1, y - yi);
  const a = hash(xi, yi, salt);
  const b = hash(xi + 1, yi, salt);
  const c = hash(xi, yi + 1, salt);
  const d = hash(xi + 1, yi + 1, salt);
  return a + (b - a) * u + (c - a) * w + (a - b - c + d) * u * w;
}

/** Three octaves of `noise`, 0..1 (mostly round the middle). */
const fbm = (x: number, y: number, salt: number): number =>
  (noise(x, y, salt) * 4 + noise(x * 2, y * 2, salt + 1) * 2 + noise(x * 4, y * 4, salt + 2)) / 7;

/** Noise swirled through noise (domain warping), as molten, etched or quenched metal swirls: 0..1. */
function swirl(x: number, y: number, scale: number, warp: number, salt: number): number {
  const sx = x * scale;
  const sy = y * scale;
  return fbm(sx + warp * fbm(sx, sy, salt + 10), sy + warp * fbm(sx + 5.2, sy + 1.3, salt + 20), salt);
}

/** A shape of a pattern's art in the metal: `s` within `box` in ink `ink` (of the finish's `inks`), lit `dv` more. */
interface Decal {
  box: Box;
  s: Sdf;
  ink: number;
  dv?: number;
}

/** A finish ready to paint at `res` px an art px: its ramps as colours, its art's shapes. */
interface Finish {
  p: Polish;
  res: number;
  body: RGB[];
  back: RGB[];
  trim: RGB[];
  glass: RGB[];
  gem: RGB[];
  inks: RGB[][];
  decals: readonly Decal[];
}

const finishOf = (p: Polish, res: number): Finish => ({
  p,
  res,
  body: colours(p.body),
  back: colours(p.back ?? p.body),
  trim: colours(p.trim),
  glass: colours(p.glass),
  gem: colours(p.gem),
  inks: (p.inks ?? []).map(colours),
  decals: p.pattern ? artOf(p.pattern) : [],
});

/** Ink `i` of `f` lit to `v`. */
const ink = (f: Finish, i: number, v: number): RGB => tone(f.inks[i] as RGB[], v);

/** The metal at x, y lit to step `v`, with the finish's pattern. */
function metal(f: Finish, x: number, y: number, v: number, back: boolean): RGB {
  const pattern = f.p.pattern;
  // A paint job's gloss: the mirror's sky and horizon pulled towards the middle.
  if (f.p.gloss !== undefined) v = 3.3 + (v - 3.3) * f.p.gloss;
  if (pattern === 'damascus') {
    const w = Math.sin(x * 1.6 + 1.8 * Math.sin(y * 0.7 + x * 0.18));
    v += 0.7 * smooth(0.8, 0.98, w) - 0.6 * smooth(0.8, 0.98, -w);
  }
  if (pattern === 'dragon') v += (fbm(x * 0.5, y * 0.5, 31) - 0.5) * 1.1;
  // The Doppler's smoke: dark clouds and bright pools swirled through each other, dragged along the blade.
  let cloud = 0;
  if (pattern === 'doppler') {
    const n = swirl(x, y, 0.18, 3.4, 5);
    cloud = swirl(x, y, 0.12, 2.4, 9);
    v += 1.2 * smooth(0.5, 0.64, n) - 1.5 * smooth(0.5, 0.36, n) + 0.5 * (noise(x * 0.35, y * 2.2, 7) - 0.5);
  }
  let c: RGB;
  if (pattern === 'fade') {
    const top = f.inks.length - 1;
    const t = clamp(((x + (y - 20) * 0.4 - 12) / 48) * top, 0, top - 0.001);
    const z = Math.floor(t);
    c = mix(ink(f, z, v), ink(f, Math.min(top, z + 1), v), smooth(0, 1, t - z));
  } else c = tone(back ? f.back : f.body, v);
  if (pattern === 'doppler' && f.inks.length) c = mix(c, ink(f, 0, v), smooth(0.42, 0.62, cloud));
  if (pattern === 'patina') {
    // Case hardening: the quench's blue over most of it, violet and gold where it ran hotter, bare steel hottest.
    const n = swirl(x, y, 0.15, 2.8, 13);
    const e = 0.025;
    c = mix(c, ink(f, 2, v), 1 - smooth(0.66 - e, 0.66 + e, n));
    c = mix(c, ink(f, 1, v), 1 - smooth(0.6 - e, 0.6 + e, n));
    c = mix(c, ink(f, 0, v - 0.3 * fbm(x * 0.8, y * 0.8, 17)), 1 - smooth(0.55 - e, 0.55 + e, n));
  }
  if (pattern === 'marble') {
    // Fire and ice: bands of its inks round the swirl, each flowing into the next.
    const L = f.inks.length;
    const t = (((swirl(x, y, 0.17, 3.6, 21) * 2.4 * L) % L) + L) % L;
    const z = Math.floor(t);
    c = mix(ink(f, z, v), ink(f, (z + 1) % L, v), smooth(0.65, 1, t - z));
  }
  if (pattern === 'pearl' || pattern === 'prism') {
    // Iridescence in the lit bands: pearl's soft pinks and blues, prism's rainbow, drifting along the gun.
    const lit = smooth(2.6, 4.2, v) * (1 - smooth(5, 5.6, v));
    const h = (x * 0.09 - y * 0.07 + 10) % 1;
    const hue =
      pattern === 'pearl'
        ? mix(rgb('i'), rgb('c'), 0.5 + 0.5 * Math.sin(h * Math.PI * 2))
        : mix(RAINBOW[Math.floor(h * 7) % 7] as RGB, RAINBOW[(Math.floor(h * 7) + 1) % 7] as RGB, (h * 7) % 1);
    c = mix(c, hue, lit * (pattern === 'pearl' ? 0.28 : 0.55));
  }
  if (pattern === 'tiger') {
    // Stripes slanting down the gun, waving, thickest in the middle of the body and tapering off its edges.
    const u = x - 0.45 * (y - 20) - 0.7 * Math.sin(y * 0.8 + x * 0.15);
    const i = Math.round(u / 4.2);
    const d = Math.abs(u - i * 4.2);
    const w = (0.55 + 0.45 * hash(i, 7, 1)) * clamp(1 - ((y - 21.5 - (hash(i, 3, 1) - 0.5) * 4) / 7.5) ** 2);
    const band = 1 - smooth(w - 0.18, w + 0.18, d);
    c = mix(c, mix(BLACK, rgb('N'), 0.4 + 0.15 * clamp(v - 3)), band * 0.92);
  }
  // Art painted on, lit as the metal is: its outlines first, then what they hold.
  for (const d of f.decals) {
    if (x < d.box[0] || x > d.box[2] || y < d.box[1] || y > d.box[3]) continue;
    const a = clamp(0.5 - d.s(x, y) * f.res);
    if (a > 0) c = mix(c, ink(f, d.ink, v + (d.dv ?? 0)), a);
  }
  return c;
}

/** Bevels: within 0.7 px of `s`'s edge, lit where it faces the light, shaded where it faces away. */
const bevel = (s: Sdf, x: number, y: number, d: number): number =>
  d > -0.7 ? 1.3 * facing(s, x, y) * (1 - -d / 0.7) : 0;

/** A cut groove `w` wide `inset` inside `s`: its dark line, and its far lip catching the light. */
function groove(s: Sdf, x: number, y: number, inset: number): number {
  const d = s(x, y) + inset;
  if (Math.abs(d) < 0.17) return -1.7;
  if (d < -0.17 && d > -0.5) return 0.8 * Math.max(0, -facing(s, x, y));
  return 0;
}

/** A small round screw head at (cx, cy) in trim, its slot across it. */
function screw(r: Raster, f: Finish, cx: number, cy: number): void {
  const s = circle(cx, cy, 0.72);
  r.fill([cx - 1, cy - 1, cx + 1, cy + 1], s, (x, y) => {
    const v = 2.3 - (x - cx + y - cy) * 1.6;
    const slot = Math.abs(x - cx - (y - cy)) < 0.14;
    return tone(f.trim, slot ? 0.4 : v);
  });
}

/** A leaf (an ellipse rx × ry) at (cx, cy) turned by `turn`. */
const leaf = (cx: number, cy: number, rx: number, ry: number, turn: number): Sdf => {
  const c = Math.cos(turn);
  const s = Math.sin(turn);
  return (x, y) => ellipse(0, 0, rx, ry)((x - cx) * c + (y - cy) * s, -(x - cx) * s + (y - cy) * c);
};

/** Scrollwork inlaid in trim: a thin vine waving from x0 to x1 about cy, a leaf off each bend, a bud at its end. */
function scroll(r: Raster, f: Finish, x0: number, x1: number, cy: number, amp: number, clip: Sdf): void {
  const period = 5;
  const k = (Math.PI * 2) / period;
  const wave = (x: number) => cy + amp * Math.sin((x - x0) * k);
  const vine: Pt[] = [];
  for (let x = x0; x <= x1 + 1e-6; x += 0.3) vine.push([x, wave(x)]);
  const parts: Sdf[] = [stroke(vine, 0.26), circle(x1 + 0.2, wave(x1), 0.38)];
  for (let x = x0 + period / 4, n = 0; x <= x1 - 0.5; x += period / 2, n++) {
    const side = n % 2 ? 1 : -1;
    parts.push(leaf(x + 0.6, wave(x) + side * 0.85, 1.05, 0.42, side * 0.6));
  }
  const line = union(...parts);
  const shape: Sdf = (x, y) => Math.max(line(x, y), clip(x, y) + 0.5);
  const bbox: Box = [x0 - 1, cy - amp - 2.4, x1 + 2, cy + amp + 2.4];
  r.fill(
    bbox,
    (x, y) => shape(x, y - 0.25),
    () => tone(f.trim, 0.3),
    0.7,
  );
  r.fill(bbox, shape, (_x, y) => tone(f.trim, 3.3 - (y - cy) * 0.3));
}

/** The gun in finish `f`. */
function paintGun(f: Finish, res: number): Raster {
  const r = new Raster(res);
  // The rim: the body's darkest, the glass's round the jar.
  const rimBody = mix(f.body[0] as RGB, BLACK, 0.25);
  const rimGlass = mix(f.glass[0] as RGB, BLACK, 0.35);
  r.fill(
    ALL,
    (x, y) => GUN(x, y) - RIM,
    (x, y) => (y < 11.6 && x > 21 && x < 43 ? rimGlass : rimBody),
  );

  // The stock: a polished slab, its butt plate lit along its back edge with a groove before it; a cut cheek panel.
  r.fill([1, 13, 23, 31], STOCK, (x, y) => {
    const d = STOCK(x, y);
    let v = profile(STOCK_ROWS, y) + bevel(STOCK, x, y, d);
    if (y > 17.6) v += streaks(x, y, 7.4) + groove(STOCK, x, y, 1.7);
    if (y > 18 && x > 2.55 && x < 3.05) v -= 1.4;
    return metal(f, x, y, v, true);
  });
  // The receiver: the same slab, darker under the collar, a cut side panel.
  const panel = box(29.2, 18.4, 45.1, 25.2, 1.2);
  r.fill([21, 13, 47, 27], RECEIVER, (x, y) => {
    const d = RECEIVER(x, y);
    let v = profile(RECEIVER_ROWS, y) + bevel(RECEIVER, x, y, d);
    if (y < 15.4 && x < 42.6) v -= 1.1 * (1 - smooth(14, 15.4, y));
    if (y > 17.6) v += streaks(x, y, 33.4) + groove(panel, x, y, 0);
    return metal(f, x, y, v, true);
  });
  // The shroud and barrel: a tapered tube with three cooling slots, a seam, then the barrel.
  const slots = union(box(47, 17.2, 48.8, 18.7, 0.6), box(50, 17.2, 51.8, 18.7, 0.6), box(52.9, 17.2, 54.6, 18.7, 0.6));
  r.fill([45, 15, 63, 25], SHROUD, (x, y) => {
    const d = SHROUD(x, y);
    let v = (x > 57 ? profile(BARREL_ROWS, y) : profile(SHROUD_ROWS, y)) + bevel(SHROUD, x, y, d);
    const s = slots(x, y);
    if (s < 0) v = 0.4 + clamp(y - 17.8) * 0.6;
    else if (s < 0.45 && y > 18.3) v += 1.4;
    if (x > 56.75 && x < 57.15) v -= 1.6;
    if (x >= 60.4) return tone(f.trim, profile(BARREL_ROWS, y) - 0.6 - (x > 61.6 ? 0.8 : 0));
    return metal(f, x, y, v, false);
  });
  // The trigger guard in trim, round the trigger in the body's metal.
  r.fill([29, 25, 37, 31], TRIGGER, (x, y) =>
    metal(f, x, y, 3 - (y - 25.6) * 0.7 + bevel(TRIGGER, x, y, TRIGGER(x, y)), false),
  );
  r.fill([29, 25, 37, 31], GUARD, (x, y) => tone(f.trim, 2.6 - (y - 27.6) * 0.5 + bevel(GUARD, x, y, GUARD(x, y))));
  // The collar: a ring of knurled trim.
  r.fill([21, 11, 43, 15], COLLAR, (x, y) => {
    const knurl = (0.5 + 0.5 * Math.cos((x * Math.PI * 2) / 0.95)) ** 3;
    const v = profile(
      [
        [11.8, 2.6],
        [12.5, 4.1],
        [13.1, 3],
        [13.7, 1.6],
        [14.3, 0.8],
      ],
      y,
    );
    return tone(f.trim, v - knurl * 0.7 + bevel(COLLAR, x, y, COLLAR(x, y)));
  });

  // The jar: polished glass, lit on its left, deep on its right with a rim of reflected light.
  r.fill([21, 1, 43, 13], JAR, (x, y) => {
    const d = JAR(x, y);
    const u = (x - 22) / 20;
    let v = 2.5 - 1.7 * u + (u > 0.86 ? 1.2 * smooth(0.86, 0.95, u) : 0) + 0.6 * bevel(JAR, x, y, d);
    if (d > -0.5 && facing(JAR, x, y) < 0) v -= 0.6;
    return tone(f.glass, v);
  });
  // Its seeds: a pile of gold seeds, each lit top-left, seen through the glass.
  const seedRamp = colours(['n', 'U', 'Y', 'y', 'e']);
  const rows: readonly (readonly [number, number, number, number])[] = [
    [11.3, 23.4, 41, 2.15],
    [10, 24.6, 40, 2.45],
    [8.8, 27, 37.6, 2.7],
    [7.7, 29.8, 34.6, 3],
  ];
  rows.forEach(([cy, x0, x1, step], row) => {
    for (let x = x0 + (row % 2) * 0.6; x <= x1; x += step) {
      const sx = x + (hash(Math.round(x * 10), row, 3) - 0.5) * 0.5;
      const sy = cy + (hash(Math.round(x * 10), row, 5) - 0.5) * 0.4;
      const tilt = (hash(Math.round(x * 10), row, 9) - 0.5) * 0.8;
      const s: Sdf = (px, py) => {
        const dx = px - sx;
        const dy = py - sy;
        const rx = dx * Math.cos(tilt) + dy * Math.sin(tilt);
        const ry = -dx * Math.sin(tilt) + dy * Math.cos(tilt);
        return (Math.hypot(rx / 1.2, ry / 0.82) - 1) * 0.82;
      };
      r.fill(
        [sx - 1.8, sy - 1.4, sx + 1.8, sy + 1.4],
        (px, py) => Math.max(s(px, py), JAR(px, py) + 0.3),
        (px, py) => {
          const v = 2.9 - (px - sx) * 0.8 - (py - sy) * 1.5;
          return tone(seedRamp, Math.hypot(px - sx + 0.4, py - sy + 0.32) < 0.26 ? 5 : v);
        },
      );
    }
  });
  // The glass over them, a little tinted, then its glints: a crescent on the shoulder, a window, a far spark.
  r.fill(
    [21, 7, 43, 13],
    (x, y) => Math.max(JAR(x, y) + 0.2, 7.6 - y),
    () => tone(f.glass, 2),
    0.18,
  );
  const crescent: Pt[] = [];
  for (let a = 3.5; a <= 4.75; a += 0.08) crescent.push([31.6 + Math.cos(a) * 7.6, 8.6 + Math.sin(a) * 5.1]);
  r.fill([22, 2, 34, 9], stroke(crescent, 0.75), () => WHITE, 0.95);
  r.fill(
    [23, 5, 26, 11],
    stroke(
      [
        [24.2, 6.4],
        [24.05, 9.6],
      ],
      0.42,
    ),
    () => WHITE,
    0.7,
  );
  r.fill([36, 3, 40, 7], circle(37.9, 4.9, 0.55), () => WHITE, 0.9);
  r.fill(
    [39, 6, 42, 12],
    stroke(
      [
        [40.7, 7.4],
        [40.8, 10.6],
      ],
      0.3,
    ),
    () => tone(f.glass, 3.6),
    0.6,
  );

  // Fittings: a medallion on the receiver (a trim bezel round a cut stone), screws on the stock and receiver.
  const bezel = circle(37.6, 21.8, 2.55);
  r.fill([34.5, 18.5, 41, 25.2], bezel, (x, y) =>
    tone(f.trim, 2.4 - (x - 37.6 + y - 21.8) * 0.75 + (Math.hypot(x - 37.6, y - 21.8) > 2.1 ? 0.3 : -0.5)),
  );
  const stone = circle(37.6, 21.8, 1.75);
  r.fill([35, 19, 40.5, 24.5], stone, (x, y) => {
    const dx = x - 37.6;
    const dy = y - 21.8;
    // Facets: the stone's light steps by which way each of its six faces turns.
    const face = Math.floor(((Math.atan2(dy, dx) + Math.PI) / (Math.PI * 2)) * 6);
    const lit = [0.4, 0.9, 1.6, 2.2, 1.4, 0.7][face] ?? 1;
    return tone(f.gem, Math.hypot(dx, dy) < 0.75 ? 1.6 - (dx + dy) * 0.6 : lit);
  });
  r.fill([36, 20, 38, 22], circle(37, 21.15, 0.38), () => WHITE);
  screw(r, f, 5.3, 20.3);
  screw(r, f, 5.3, 26.4);

  // The kill counter's window under the cooling slots: dark glass in a trim bezel, its digits' segments unlit.
  const [cx0, cy0, cx1, cy1] = COUNTER_BOX;
  r.fill([cx0 - 0.5, cy0 - 0.5, cx1 + 0.5, cy1 + 0.5], COUNTER_RIM, (x, y) =>
    tone(f.trim, 2.2 - (y - (cy0 + cy1) / 2) * 0.9 + bevel(COUNTER_RIM, x, y, COUNTER_RIM(x, y))),
  );
  r.fill(COUNTER_BOX, COUNTER_GLASS, (x, y) => {
    // A deep screen, a faint sky reflected in its upper left.
    const glint = 0.5 * clamp(1 - Math.abs(x + 1.4 * (y - cy0) - cx0 - 1.6) / 0.9);
    return mix(mix(rgb('k'), rgb('1'), 0.35 + 0.35 * (1 - (y - cy0) / (cy1 - cy0))), rgb('3'), glint * 0.45);
  });
  r.fill(COUNTER_BOX, segments(-1), () => mix(rgb('k'), rgb('N'), 0.9), 0.75);

  // Patterns laid over the metal (the counter keeps its window clear: the shroud's scroll runs along the receiver's top).
  if (f.p.pattern === 'scroll') {
    scroll(r, f, 6.6, 13.2, 23.1, 1.1, STOCK);
    scroll(r, f, 30.6, 34.6, 21.8, 1.2, panel);
    scroll(r, f, 23.4, 42.2, 16.3, 0.55, RECEIVER);
  }
  if (f.p.pattern === 'stars') {
    for (let n = 0; n < 34; n++) {
      const x = 3 + hash(n, 1, 11) * 58;
      const y = 15 + hash(n, 2, 11) * 14;
      if (GUN(x, y) > -0.6 || JAR(x, y) < 0.5 || COLLAR(x, y) < 0.3 || bezel(x, y) < 0.3) continue;
      if (COUNTER_RIM(x, y) < 0.3) continue;
      const big = hash(n, 3, 11) < 0.25;
      const s = big ? star(x, y, 1.3, 0.22) : circle(x, y, 0.2 + hash(n, 4, 11) * 0.14);
      r.fill([x - 1.6, y - 1.6, x + 1.6, y + 1.6], s, () => (hash(n, 5, 11) < 0.5 ? WHITE : rgb('c')), 0.9);
    }
  }
  return r;
}

/** A four-point star at (cx, cy): rays `len` long, `w` wide at its heart, tapering; turned by `turn`. */
function star(cx: number, cy: number, len: number, w: number, turn = 0): Sdf {
  const c = Math.cos(turn);
  const s = Math.sin(turn);
  return (x, y) => {
    const dx = (x - cx) * c + (y - cy) * s;
    const dy = -(x - cx) * s + (y - cy) * c;
    const a = Math.abs(dx);
    const b = Math.abs(dy);
    const ray1 = b - w * Math.max(0, 1 - a / len);
    const ray2 = a - w * Math.max(0, 1 - b / len);
    return Math.min(Math.max(ray1, a - len), Math.max(ray2, b - len), Math.hypot(dx, dy) - w * 1.4);
  };
}

// --- The legends' art (ADR-0148 amended) ---------------------------------------------------------------------------

/** A smooth curve through `c` (Catmull-Rom), `per` points a span. */
function spline(c: readonly Pt[], per = 6): Pt[] {
  const out: Pt[] = [];
  const at = (i: number) => c[Math.max(0, Math.min(c.length - 1, i))] as Pt;
  for (let i = 0; i < c.length - 1; i++) {
    const [p0, p1, p2, p3] = [at(i - 1), at(i), at(i + 1), at(i + 2)];
    for (let k = 0; k < per; k++) {
      const t = k / per;
      const t2 = t * t;
      const t3 = t2 * t;
      const f = (a: number, b: number, c2: number, d: number) =>
        0.5 * (2 * b + (c2 - a) * t + (2 * a - 5 * b + 4 * c2 - d) * t2 + (3 * b - a - 3 * c2 + d) * t3);
      out.push([f(p0[0], p1[0], p2[0], p3[0]), f(p0[1], p1[1], p2[1], p3[1])]);
    }
  }
  out.push(at(c.length - 1));
  return out;
}

/** A tube along `pts`, `width(t)` across at t (0 at its first point, 1 at its last), and its box. */
function tube(pts: readonly Pt[], width: (t: number) => number): { s: Sdf; box: Box } {
  const lens = pts.slice(1).map((p, i) => Math.hypot(p[0] - (pts[i] as Pt)[0], p[1] - (pts[i] as Pt)[1]));
  const total = lens.reduce((a, b) => a + b, 0);
  const widest = Math.max(...pts.map((_, i) => width(i / (pts.length - 1)))) / 2 + 0.6;
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const box: Box = [
    Math.min(...xs) - widest,
    Math.min(...ys) - widest,
    Math.max(...xs) + widest,
    Math.max(...ys) + widest,
  ];
  // Its outline, fill and what is cut into it ask the same px in turn: the last answer is kept.
  let last = [Number.NaN, Number.NaN, 0];
  const s: Sdf = (x, y) => {
    if (x === last[0] && y === last[1]) return last[2] as number;
    let d = Number.POSITIVE_INFINITY;
    let run = 0;
    for (let i = 1; i < pts.length; i++) {
      const [ax, ay] = pts[i - 1] as Pt;
      const [bx, by] = pts[i] as Pt;
      const len = lens[i - 1] as number;
      // Far from this span: past its ends by more than the tube is wide.
      if (
        x < Math.min(ax, bx) - widest ||
        x > Math.max(ax, bx) + widest ||
        y < Math.min(ay, by) - widest ||
        y > Math.max(ay, by) + widest
      ) {
        run += len;
        continue;
      }
      const ex = bx - ax;
      const ey = by - ay;
      const t = clamp(((x - ax) * ex + (y - ay) * ey) / (len * len || 1));
      d = Math.min(d, Math.hypot(x - ax - ex * t, y - ay - ey * t) - width((run + t * len) / total) / 2);
      run += len;
    }
    last = [x, y, d];
    return d;
  };
  return { s, box };
}

/** `s` (drawn round 0, 0) moved to (cx, cy), turned by `turn`, scaled `k`. */
const placed =
  (s: Sdf, cx: number, cy: number, turn = 0, k = 1): Sdf =>
  (x, y) => {
    const c = Math.cos(turn);
    const sn = Math.sin(turn);
    const dx = (x - cx) / k;
    const dy = (y - cy) / k;
    return s(dx * c + dy * sn, -dx * sn + dy * c) * k;
  };

/** A pointed petal or leaf from its base at 0, 0 up to its tip `len` away, `w` across at its widest. */
function petal(len: number, w: number): Sdf {
  const a = len / 2;
  const b = w / 2;
  const r = (a * a + b * b) / (2 * b);
  const c = r - b;
  return (x, y) => Math.max(Math.hypot(x - c, y + a) - r, Math.hypot(x + c, y + a) - r);
}

/** A quadratic curve from `a` through control `c` to `b`, `n` spans. */
const curve = (a: Pt, c: Pt, b: Pt, n = 8): Pt[] =>
  Array.from({ length: n + 1 }, (_, i) => {
    const t = i / n;
    const u = 1 - t;
    return [u * u * a[0] + 2 * u * t * c[0] + t * t * b[0], u * u * a[1] + 2 * u * t * c[1] + t * t * b[1]] as Pt;
  });

const grow = (b: Box, d: number): Box => [b[0] - d, b[1] - d, b[2] + d, b[3] + d];
/** The box round some points. */
const boxOf = (pts: readonly Pt[]): Box => [
  Math.min(...pts.map((p) => p[0])),
  Math.min(...pts.map((p) => p[1])),
  Math.max(...pts.map((p) => p[0])),
  Math.max(...pts.map((p) => p[1])),
];

/** `s` in ink `fill` inside an outline `w` wide in ink `line` (lit `dv` more). */
const inked = (box: Box, s: Sdf, fill: number, line: number, w = 0.3, dv = 0): Decal[] => [
  { box: grow(box, w + 0.1), s: (x, y) => s(x, y) - w, ink: line },
  { box, s, ink: fill, dv },
];

/** A filled polygon `pts` in ink `fill`, outlined in ink `line`. */
const shape = (pts: readonly Pt[], fill: number, line: number, w = 0.3, dv = 0): Decal[] =>
  inked(boxOf(pts), poly(pts), fill, line, w, dv);

/** Scales: a diagonal lattice of lines, ≈1.3 art px apart. */
const lattice: Sdf = (x, y) => {
  const u = Math.abs(((x + y) * 0.75) % 1) - 0.5;
  const w = Math.abs(((x - y) * 0.75 + 100) % 1) - 0.5;
  return (Math.min(Math.abs(Math.abs(u) - 0.5), Math.abs(Math.abs(w) - 0.5)) - 0.06) / 0.75;
};

/** A flame licking `h` up from (x, y), `w` across at its foot, curling `lean` back as it thins to its tip. */
const flame = (x: number, y: number, h: number, lean: number, w: number) =>
  tube(
    spline(
      [
        [x, y],
        [x - lean * 0.15 + w * 0.3, y - h * 0.35],
        [x - lean * 0.6 - w * 0.2, y - h * 0.7],
        [x - lean, y - h],
      ],
      5,
    ),
    (t) => w * (1 - t) ** 0.85 + 0.08,
  );

/** Dragon Bloom: a green dragon with gold scales and dark red wings coiled along a cream stone gun, a Celtic knot by its tail. */
function dragonArt(): Decal[] {
  const [SCALE, GOLDEN, WING, LINE] = [0, 1, 2, 3];
  const out: Decal[] = [];
  // The wing behind it: raised over the receiver's top, its membrane scalloped between the fingers.
  const wrist: Pt = [32.4, 14.8];
  const fingers: Pt[] = [
    [15.6, 15],
    [19.4, 17.6],
    [23.6, 19.4],
  ];
  const wing: Pt[] = [
    [30.2, 21],
    [31.6, 17.4],
    wrist,
    [24, 14.6],
    ...curve(fingers[0] as Pt, [18.8, 19], fingers[1] as Pt, 5),
    ...curve(fingers[1] as Pt, [22.8, 20.8], fingers[2] as Pt, 5).slice(1),
    ...curve(fingers[2] as Pt, [27, 22.4], [30.2, 21], 5).slice(1, -1),
  ];
  out.push(...shape(wing, WING, LINE));
  for (const f of fingers) out.push({ box: grow(boxOf([wrist, f]), 0.4), s: stroke([wrist, f], 0.34), ink: LINE });
  // Its body: tail curled on the stock, coiling forward, the neck rising over the medallion.
  const spine = spline([
    [4.6, 28.2],
    [3.6, 25.6],
    [5.2, 23.4],
    [8.2, 23.6],
    [10.8, 25.6],
    [14, 27],
    [17.6, 26.6],
    [20.8, 24.6],
    [24, 22.6],
    [27.6, 22.2],
    [31, 23],
    [33.6, 22.4],
    [35.4, 19.8],
    [37.8, 17.8],
    [40.6, 17.2],
  ]);
  const width = (t: number) => 0.5 + 3.1 * smooth(0, 0.55, t) - 0.9 * smooth(0.72, 0.96, t);
  const body = tube(
    spline(
      spine.filter((_, i) => i % 2 === 0),
      2,
    ),
    width,
  );
  out.push(...inked(body.box, body.s, SCALE, LINE));
  // Its scales cut into the green, a gold belly, a ridge of gold spines along its back.
  out.push({ box: body.box, s: (x, y) => Math.max(lattice(x, y), body.s(x, y) + 0.35), ink: SCALE, dv: -1.3 });
  const belly = tube(
    spine.filter((_, i) => i % 2 === 0).map(([x, y]) => [x, y + 0.8] as Pt),
    (t) => width(t) * 0.4 * smooth(0.08, 0.2, t) * (1 - smooth(0.85, 0.95, t)),
  );
  out.push({ box: belly.box, s: (x, y) => Math.max(belly.s(x, y), body.s(x, y) + 0.2), ink: GOLDEN });
  for (let i = 10; i < spine.length - 8; i += 3) {
    const [ax, ay] = spine[i - 1] as Pt;
    const [bx, by] = spine[i + 1] as Pt;
    const [px, py] = spine[i] as Pt;
    const len = Math.hypot(bx - ax, by - ay) || 1;
    const tx = (bx - ax) / len;
    const ty = (by - ay) / len;
    // The side away from the ground.
    const up = ty * 1 - tx * 0 > 0 ? 1 : -1;
    const nx = ty * up;
    const ny = -tx * up;
    const half = width(i / (spine.length - 1)) / 2;
    const base: Pt = [px + nx * (half - 0.25), py + ny * (half - 0.25)];
    const spike: Pt[] = [
      [base[0] - tx * 0.65, base[1] - ty * 0.65],
      [base[0] + nx * 1.25 - tx * 0.6, base[1] + ny * 1.25 - ty * 0.6],
      [base[0] + tx * 0.65, base[1] + ty * 0.65],
    ];
    out.push(...shape(spike, GOLDEN, LINE, 0.22));
  }
  // The head, jaws open towards the muzzle, horns swept back, a gold eye.
  for (const horn of [
    [
      [37.6, 14.6],
      [41.6, 15.3],
      [40.4, 16],
    ],
    [
      [37.4, 16.6],
      [40.6, 16.3],
      [40, 17.2],
    ],
  ] as Pt[][])
    out.push(...shape(horn, GOLDEN, LINE, 0.22));
  out.push(
    ...shape(
      [
        [39.2, 16.2],
        [41, 15],
        [43.2, 15.2],
        [46.6, 16.2],
        [47.2, 17],
        [44.6, 17.6],
        [42.4, 18],
        [45.6, 18.45],
        [45.9, 18.95],
        [42.6, 19.2],
        [39.6, 19.6],
      ],
      SCALE,
      LINE,
    ),
  );
  out.push(...inked([41.2, 15.7, 42.8, 16.9], ellipse(42, 16.3, 0.65, 0.42), GOLDEN, LINE, 0.15, 1));
  out.push({ box: [45.4, 16, 46.4, 17], s: circle(45.9, 16.5, 0.22), ink: LINE });
  // A Celtic knot by its tail: three loops woven in a ring.
  const knot = union(
    (x, y) => Math.abs(circle(10.2, 19.3, 3.1)(x, y)) - 0.32,
    ...[0, 1, 2].map((k) => {
      const a = -Math.PI / 2 + (k * Math.PI * 2) / 3;
      const c = circle(10.2 + Math.cos(a) * 1.25, 19.3 + Math.sin(a) * 1.25, 1.7);
      return (x: number, y: number) => Math.abs(c(x, y)) - 0.3;
    }),
  );
  out.push(...inked([6.6, 15.7, 13.8, 22.9], knot, GOLDEN, LINE, 0.25, 0.4));
  return out;
}

/** Howl: a black wolf howling on the receiver of a red gun, outlined in fire, flames licking back along the stock. */
function howlArt(): Decal[] {
  const [WOLF, FLAME, LINE, EYE] = [0, 1, 2, 3];
  const out: Decal[] = [];
  // Flames licking up off the stock's foot and curling back, the gun rushing forward: the big ones, the small between.
  const CORE = 4;
  const flames = [
    [5.2, 31, 8.5, 2.6, 2.6],
    [8.4, 30, 10, 3, 2.8],
    [11.6, 28.6, 9, 2.8, 2.6],
    [14.8, 27, 10.4, 3.2, 2.8],
    [18, 26.4, 8, 2.6, 2.4],
    [20.8, 26.4, 5.5, 2, 1.8],
    [42.4, 27, 6.4, 2.2, 2.2],
    [6.8, 30.6, 5.2, 1.8, 1.6],
    [10, 29.4, 6, 2, 1.7],
    [13.2, 28, 5.8, 2, 1.6],
    [16.4, 26.8, 5.6, 2, 1.6],
    [44.6, 27, 4.4, 1.6, 1.5],
  ] as const;
  for (const [x, y, h, lean, w] of flames) {
    const f = flame(x, y, h, lean, w);
    out.push(...inked(f.box, f.s, FLAME, LINE, 0.3));
  }
  for (const [x, y, h, lean, w] of flames) {
    const f = flame(x + 0.1, y, h * 0.62, lean * 0.62, w * 0.45);
    out.push({ box: f.box, s: f.s, ink: CORE });
  }
  // The wolf, head thrown back, muzzle to the sky, its ruff ragged.
  const wolf: Pt[] = [
    [23.4, 26],
    [24.1, 24],
    [23.1, 23.4],
    [24.4, 22.2],
    [23.7, 21.3],
    [25.1, 20.3],
    [24.8, 19.4],
    [25.6, 18.7],
    [25.9, 16.6],
    [26.7, 15.1],
    [28, 17.4],
    [29.4, 17.6],
    [31.5, 16.8],
    [34.4, 15.3],
    [34.9, 16.2],
    [32.8, 17.9],
    [33.2, 18.1],
    [30.9, 19.2],
    [33.4, 19.1],
    [32.1, 20.2],
    [30.4, 21],
    [29.5, 21.6],
    [30.1, 22.2],
    [29, 23.2],
    [29.6, 24.1],
    [28.6, 26],
  ];
  out.push(...inked(boxOf(wolf), poly(wolf), WOLF, FLAME, 0.45, 0.3));
  // Its fangs, its eye burning, the fur's lines.
  out.push(
    ...shape(
      [
        [32.4, 17.95],
        [32.1, 18.75],
        [31.8, 18.1],
      ],
      EYE,
      LINE,
      0,
      2.5,
    ),
  );
  out.push(...inked([27.8, 18, 29.6, 19.2], placed(ellipse(0, 0, 0.75, 0.3), 28.7, 18.6, -0.45), EYE, LINE, 0, 1.4));
  for (const pts of [
    [
      [26.2, 20.4],
      [27.6, 19.8],
    ],
    [
      [25.8, 22.6],
      [27.6, 21.4],
    ],
    [
      [26.4, 24.4],
      [28, 23.2],
    ],
  ] as Pt[][])
    out.push({ box: grow(boxOf(pts), 0.4), s: stroke(pts, 0.22), ink: WOLF, dv: 1.6 });
  return out;
}

/** Fire Viper: a gold feathered serpent winding the length of a dark green gun, red plumes, jaws open. */
function serpentArt(): Decal[] {
  const [GOLDEN, PLUME, LINE, GREEN] = [0, 1, 2, 3];
  const out: Decal[] = [];
  const spine = spline([
    [3.6, 26.8],
    [6, 23.2],
    [9.6, 21.6],
    [13.2, 23.4],
    [16.8, 25.6],
    [20.6, 24.8],
    [24, 21.8],
    [27.6, 19.8],
    [31.2, 20.4],
    [34.2, 18.4],
    [37.6, 17.1],
    [40.6, 17.1],
  ]);
  const width = (t: number) => 0.5 + 1.9 * smooth(0, 0.5, t) + 0.2 * smooth(0.8, 1, t);
  // Green feathered fins along its back, pointing back down its length.
  for (let i = 5; i < spine.length - 6; i += 7) {
    const [ax, ay] = spine[i - 1] as Pt;
    const [bx, by] = spine[i + 1] as Pt;
    const [px, py] = spine[i] as Pt;
    const back = Math.atan2(ay - by, ax - bx);
    const fin = placed(petal(3, 1), px, py - width(i / (spine.length - 1)) * 0.3, back + Math.PI / 2 - 0.7);
    out.push(...inked([px - 3.4, py - 3.6, px + 3.4, py + 2], fin, GREEN, LINE, 0.22, 0.6));
  }
  const body = tube(spine, width);
  out.push(...inked(body.box, body.s, GOLDEN, LINE));
  // Its scales cut into the gold.
  out.push({ box: body.box, s: (x, y) => Math.max(lattice(x, y), body.s(x, y) + 0.3), ink: GOLDEN, dv: -1.6 });
  // Red plumes streaming back off its head.
  for (const [turn, len] of [
    [-1.05, 3.6],
    [-1.35, 4.2],
    [-1.65, 3.8],
    [-1.95, 3],
  ] as const) {
    const plume = placed(petal(len, 1.05), 40.4, 16.6, turn);
    out.push(...inked([34.6, 12, 41.6, 21], plume, PLUME, LINE, 0.22, 0.4));
  }
  // The head, jaws open towards the muzzle, a fang, its eye.
  out.push(
    ...shape(
      [
        [39.4, 16.2],
        [41.6, 15.4],
        [44.6, 15.9],
        [46.4, 16.9],
        [43.8, 17.5],
        [42.4, 17.9],
        [45, 18.45],
        [45.3, 18.95],
        [42.2, 19.25],
        [39.6, 19.2],
      ],
      GOLDEN,
      LINE,
    ),
  );
  out.push(
    ...shape(
      [
        [44.2, 17.35],
        [44.6, 18.3],
        [43.6, 17.6],
      ],
      PLUME,
      LINE,
      0,
      2.4,
    ),
  );
  out.push(...inked([41.4, 16, 42.8, 17], ellipse(42.1, 16.5, 0.5, 0.36), PLUME, LINE, 0.15, 1));
  return out;
}

/** Night Lotus: pink lotus flowers in bloom on a deep green gun, gold leaves sweeping between them. */
function lotusArt(): Decal[] {
  const [PINK, GOLDEN, LINE, GREEN] = [0, 1, 2, 3];
  const out: Decal[] = [];
  const leaf = (cx: number, cy: number, len: number, w: number, turn: number, i: number) => {
    const s = placed(petal(len, w), cx, cy, turn);
    const r = len + 0.6;
    out.push(...inked([cx - r, cy - r, cx + r, cy + r], s, i, LINE, 0.25));
    // Its midrib.
    const tip: Pt = [cx + Math.sin(turn) * len * 0.85, cy - Math.cos(turn) * len * 0.85];
    out.push({ box: grow(boxOf([[cx, cy], tip]), 0.4), s: stroke([[cx, cy], tip], 0.2), ink: i, dv: -1.4 });
  };
  // Leaves sweeping along the gun under the flowers.
  leaf(15, 26.6, 8.6, 3, 1.1, GOLDEN);
  leaf(8.4, 26.8, 6.4, 2.4, -1.2, GREEN);
  leaf(18.6, 22.4, 6, 2.2, 0.75, GREEN);
  leaf(33, 25.8, 8, 2.8, 1.15, GREEN);
  leaf(26.6, 26.2, 6, 2.3, -1.05, GOLDEN);
  leaf(44.6, 24.6, 6.4, 2.2, 1, GOLDEN);
  leaf(3.6, 22, 4.6, 1.8, 0.35, GOLDEN);
  // Two flowers: their outer petals first, the inner over them, a gold heart at their foot.
  const flower = (cx: number, cy: number, k: number) => {
    for (const [turn, len, w] of [
      [1.45, 2.6, 1.3],
      [0.95, 3.3, 1.5],
      [0.45, 3.9, 1.6],
      [0, 4.3, 1.7],
    ] as const)
      for (const t of turn ? [-turn, turn] : [0]) {
        const s = placed(petal(len * k, w * k), cx, cy, t);
        const r = (len + 0.6) * k;
        out.push(...inked([cx - r, cy - r, cx + r, cy + 0.6], s, PINK, LINE, 0.22, 0.5 - Math.abs(t) * 0.6));
      }
    out.push(
      ...inked(
        [cx - 1.8 * k, cy - 1, cx + 1.8 * k, cy + 1],
        ellipse(cx, cy, 1.5 * k, 0.65 * k),
        GOLDEN,
        LINE,
        0.2,
        0.8,
      ),
    );
  };
  flower(11.6, 26, 1.4);
  flower(31, 25.2, 1.2);
  return out;
}

/** Thunder Spear: a spear in gold down the length of a deep blue gun, runes cut along the stock. */
function runesArt(): Decal[] {
  const [GOLDEN, LINE] = [0, 1];
  const out: Decal[] = [];
  /** A cut line: its shadow, then the gold in it. */
  const cut = (pts: Pt[], w: number, dv = 0.6) => {
    const b = grow(boxOf(pts), w + 0.6);
    out.push({
      box: b,
      s: stroke(
        pts.map(([x, y]) => [x + 0.18, y + 0.24] as Pt),
        w + 0.1,
      ),
      ink: LINE,
    });
    out.push({ box: b, s: stroke(pts, w), ink: GOLDEN, dv });
  };
  // The spear: its shaft along the top of the stock and receiver, bound under its head, the blade on the shroud.
  cut(
    [
      [4.6, 17.9],
      [44.6, 17.9],
    ],
    0.62,
    -1,
  );
  out.push(...inked([3.4, 16.8, 5.6, 19], circle(4.5, 17.9, 0.85), GOLDEN, LINE, 0.22, 0.8));
  for (let x = 42.2; x < 45; x += 0.7)
    out.push({
      box: [x - 0.6, 16.8, x + 0.6, 19],
      s: stroke(
        [
          [x - 0.25, 17.15],
          [x + 0.25, 18.65],
        ],
        0.26,
      ),
      ink: LINE,
    });
  const blade = placed(petal(9.2, 2), 44.9, 17.25, Math.PI / 2);
  out.push(...inked([44.4, 15.8, 54.6, 18.8], blade, GOLDEN, LINE, 0.25, -0.9));
  out.push({
    box: [45, 16.8, 53, 17.7],
    s: stroke(
      [
        [45.4, 17.25],
        [52.4, 17.25],
      ],
      0.18,
    ),
    ink: LINE,
  });
  // Runes along the stock and the receiver's back: ᚠ ᛉ ᛟ ᛏ ᛞ ᚱ.
  const rune = (cx: number, top: number, h: number, kind: string) => {
    const w = h * 0.55;
    const [l, r, b] = [cx - w / 2, cx + w / 2, top + h];
    const m = top + h / 2;
    const strokes: Record<string, Pt[][]> = {
      fehu: [
        [
          [l + w * 0.2, top],
          [l + w * 0.2, b],
        ],
        [
          [l + w * 0.2, top + h * 0.35],
          [r, top],
        ],
        [
          [l + w * 0.2, m + h * 0.05],
          [r, top + h * 0.3],
        ],
      ],
      algiz: [
        [
          [cx, top],
          [cx, b],
        ],
        [
          [cx, m],
          [l, top],
        ],
        [
          [cx, m],
          [r, top],
        ],
      ],
      othala: [
        [
          [cx, top],
          [r, top + h * 0.3],
          [l, b],
        ],
        [
          [cx, top],
          [l, top + h * 0.3],
          [r, b],
        ],
      ],
      tiwaz: [
        [
          [cx, top],
          [cx, b],
        ],
        [
          [l, top + h * 0.3],
          [cx, top],
          [r, top + h * 0.3],
        ],
      ],
      dagaz: [
        [
          [l, top],
          [l, b],
          [r, top],
          [r, b],
          [l, top],
        ],
      ],
      raido: [
        [
          [l, top],
          [l, b],
        ],
        [
          [l, top],
          [r, top + h * 0.25],
          [l, m],
          [r, b],
        ],
      ],
    };
    for (const pts of strokes[kind] ?? []) cut(pts, 0.4);
  };
  rune(7.6, 19.8, 4.2, 'fehu');
  rune(11.1, 19.8, 4.2, 'algiz');
  rune(14.6, 19.8, 4.2, 'othala');
  rune(18.1, 19.8, 4.2, 'tiwaz');
  rune(24.4, 19.8, 3.6, 'dagaz');
  rune(27.4, 19.8, 3.6, 'raido');
  return out;
}

/** Spider Lily: black spider webs spun over a deep red gun. */
function webArt(): Decal[] {
  const web = (cx: number, cy: number, reach: number, spokes: number, turn: number, gap: number): Sdf => {
    const angles = Array.from(
      { length: spokes },
      (_, i) => turn + ((i + (hash(i, spokes, 41) - 0.5) * 0.35) * Math.PI * 2) / spokes,
    );
    return (x, y) => {
      const dx = x - cx;
      const dy = y - cy;
      const r = Math.hypot(dx, dy);
      if (r > reach + 1) return r - reach;
      let a = Math.atan2(dy, dx) - turn;
      a = ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      // The spokes either side of it.
      let i = 0;
      while (i < spokes - 1 && (angles[i + 1] as number) - turn <= a) i++;
      const a0 = angles[i] as number;
      const a1 = (angles[(i + 1) % spokes] as number) + (i + 1 === spokes ? Math.PI * 2 : 0);
      const at = a + turn;
      let d = Math.min(r * Math.abs(Math.sin(at - a0)), r * Math.abs(Math.sin(a1 - at)));
      // The spiral's threads: chords between the spokes, sagging in towards the middle.
      const j0 = Math.max(1, Math.round(r / gap) - 1);
      for (let j = j0; j <= j0 + 2; j++) {
        const R = j * gap;
        if (R > reach) break;
        const p: Pt = [cx + Math.cos(a0) * R, cy + Math.sin(a0) * R];
        const q: Pt = [cx + Math.cos(a1) * R, cy + Math.sin(a1) * R];
        const ex = q[0] - p[0];
        const ey = q[1] - p[1];
        const len = Math.hypot(ex, ey) || 1;
        const u = clamp(((x - p[0]) * ex + (y - p[1]) * ey) / (len * len));
        const out = ((x - p[0]) * ey - (y - p[1]) * ex) / len;
        d = Math.min(d, Math.abs(out + 0.16 * len * 4 * u * (1 - u)));
      }
      return d - 0.14;
    };
  };
  return [
    { box: [1, 12, 26, 31], s: web(13.6, 21.4, 11.5, 9, 0.2, 1.7), ink: 0 },
    { box: [24, 12, 63, 27], s: web(37, 19.6, 13, 10, 0.5, 1.8), ink: 0 },
  ];
}

const ART: Partial<Record<Pattern, () => Decal[]>> = {
  dragon: dragonArt,
  howl: howlArt,
  serpent: serpentArt,
  lotus: lotusArt,
  runes: runesArt,
  web: webArt,
};
/** Each pattern's art, built once. */
const ARTS = new Map<Pattern, readonly Decal[]>();
function artOf(p: Pattern): readonly Decal[] {
  let a = ARTS.get(p);
  if (!a) {
    a = ART[p]?.() ?? [];
    ARTS.set(p, a);
  }
  return a;
}

// --- Pictures ------------------------------------------------------------------------------------------------------

/** A picture: straight-alpha RGBA, `w` × `h` px. */
export interface Painted {
  rgba: Uint8ClampedArray;
  w: number;
  h: number;
}

/** `kills` lit on the counter: orange segments glowing in the glass. */
function count(r: Raster, kills: number): void {
  const lit = segments(kills);
  const glow =
    (d: number): Sdf =>
    (x, y) =>
      Math.max(lit(x, y) - d, COUNTER_GLASS(x, y));
  r.fill(COUNTER_BOX, glow(0.7), () => rgb('o'), 0.22);
  r.fill(COUNTER_BOX, glow(0.3), () => rgb('o'), 0.5);
  r.fill(COUNTER_BOX, lit, () => mix(rgb('Y'), rgb('y'), 0.4));
}

/** The gun at rest in finish `key`, at `res` px an art px; with `kills`, its owner's lit on the counter (review sheets). */
export function paintPolished(key: PolishName, res = POLISH_RES, kills?: number): Painted {
  if (kills === undefined) return restingGun(key, res).painted();
  const r = new Raster(res, restingGun(key, res).px);
  count(r, kills);
  return r.painted();
}

/** `kills` lit on the counter: a picture of `COUNTER_BOX`, clear but for the digits and their glow, laid over the gun. */
export function paintCount(kills: number, res = POLISH_RES): Painted {
  const [x0, y0, x1, y1] = COUNTER_BOX;
  const r = new Raster(res, undefined, [x1 - x0, y1 - y0], x0, y0);
  count(r, kills);
  return r.painted();
}

/** The gun at rest in each finish and size, painted once. */
const RESTING = new Map<string, Raster>();

function restingGun(key: PolishName, res: number): Raster {
  const id = `${key}@${res}`;
  let base = RESTING.get(id);
  if (!base) {
    base = paintGun(finishOf(POLISHED[key], res), res);
    RESTING.set(id, base);
  }
  return base;
}
